import React, { useCallback, useEffect, useState } from 'react';
import {
    Box, Paper, Typography, Button, LinearProgress, Checkbox, FormControlLabel,
    Collapse, IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../api/axios';
import {
    getActivationProgress,
    setActivationProgress,
    dismissActivation,
    isActivationComplete,
} from '../utils/activationProgress';

const STEPS = [
    { key: 'connected', label: 'חברו תיבת מייל', path: '/webmail?connect=1' },
    { key: 'sent', label: 'שלחו מייל ראשון', path: '/webmail' },
    { key: 'aiReply', label: 'נסו תשובת AI', path: '/webmail' },
    { key: 'automation', label: 'הפעילו אוטומציה', path: '/automations' },
];

/**
 * In-product activation checklist — connect → send → AI reply → automation.
 * Persists in localStorage; syncs with server signals on mount.
 */
const ActivationChecklist = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [progress, setProgress] = useState(getActivationProgress);
    const [open, setOpen] = useState(true);

    const refreshFromServer = useCallback(async () => {
        const current = getActivationProgress();
        // Dismissed / completed users never see the checklist — don't probe the
        // server (a 402 from /automations would auto-open the upgrade dialog).
        if (current.dismissed || isActivationComplete(current)) return;

        const next = { ...current };
        // suppressModuleUpsell: background probes must never trigger the global
        // upgrade dialog (module:upgrade-required) for trial-expired orgs.
        try {
            const accountsRes = await api.get('/mail/accounts', { suppressModuleUpsell: true }).catch(() => null);
            const accounts = accountsRes?.data?.accounts || accountsRes?.data || [];
            if (Array.isArray(accounts) && accounts.length > 0) next.connected = true;
        } catch { /* ignore */ }
        if (!next.automation) {
            try {
                const autoRes = await api.get('/automations', { suppressModuleUpsell: true }).catch(() => null);
                const list = autoRes?.data?.automations || autoRes?.data || [];
                if (Array.isArray(list) && list.length > 0) next.automation = true;
            } catch { /* ignore */ }
        }
        setActivationProgress(next);
        setProgress(next);
    }, []);

    useEffect(() => {
        refreshFromServer();
        const onUpdate = (e) => setProgress(e.detail || getActivationProgress());
        window.addEventListener('emailLion:activation', onUpdate);
        return () => window.removeEventListener('emailLion:activation', onUpdate);
    }, [refreshFromServer]);

    // Don't show on marketing / auth pages
    if (!location.pathname.startsWith('/webmail')
        && !location.pathname.startsWith('/automations')
        && !location.pathname.startsWith('/ai-agent')
        && !location.pathname.startsWith('/calendar')
        && !location.pathname.startsWith('/settings')
        && !location.pathname.startsWith('/filing')
        && !location.pathname.startsWith('/dashboard')) {
        return null;
    }

    if (progress.dismissed || isActivationComplete(progress)) return null;

    const doneCount = STEPS.filter((s) => progress[s.key]).length;
    const pct = Math.round((doneCount / STEPS.length) * 100);

    return (
        <Paper
            elevation={8}
            sx={{
                position: 'fixed',
                bottom: { xs: 72, sm: 24 },
                left: { xs: 12, sm: 24 },
                zIndex: 1300,
                width: { xs: 'calc(100% - 24px)', sm: 320 },
                maxWidth: 360,
                borderRadius: 2,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 1,
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    cursor: 'pointer',
                }}
                onClick={() => setOpen((v) => !v)}
            >
                <RocketLaunchIcon fontSize="small" />
                <Typography variant="subtitle2" fontWeight="bold" sx={{ flex: 1 }}>
                    התחלה מהירה ({doneCount}/{STEPS.length})
                </Typography>
                <IconButton
                    size="small"
                    sx={{ color: 'inherit' }}
                    aria-label="סגור checklist"
                    onClick={(e) => {
                        e.stopPropagation();
                        dismissActivation();
                        setProgress(getActivationProgress());
                    }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>
            <LinearProgress variant="determinate" value={pct} sx={{ height: 4 }} />
            <Collapse in={open}>
                <Box sx={{ p: 1.5 }}>
                    {STEPS.map((step) => (
                        <FormControlLabel
                            key={step.key}
                            sx={{
                                display: 'flex',
                                m: 0,
                                mb: 0.5,
                                opacity: progress[step.key] ? 0.65 : 1,
                                '& .MuiFormControlLabel-label': {
                                    textDecoration: progress[step.key] ? 'line-through' : 'none',
                                    fontSize: '0.875rem',
                                },
                            }}
                            control={
                                <Checkbox
                                    size="small"
                                    checked={!!progress[step.key]}
                                    disabled={!!progress[step.key]}
                                    onChange={() => {
                                        if (!progress[step.key]) navigate(step.path);
                                    }}
                                />
                            }
                            label={step.label}
                            onClick={() => {
                                if (!progress[step.key]) navigate(step.path);
                            }}
                        />
                    ))}
                    <Button size="small" fullWidth sx={{ mt: 1 }} onClick={() => navigate('/webmail?connect=1')}>
                        המשך הגדרה
                    </Button>
                </Box>
            </Collapse>
        </Paper>
    );
};

export default ActivationChecklist;
